/**
 * Helpers that open the cross-engine migration workspace tab.
 *
 * A migration can be long-running, so it gets its own workspace surface with
 * live progress + cancel. These helpers build the `addTab` payload that the
 * Object Explorer context menus and the command palette both use, so the two
 * entry points stay in lock-step on title / connectionInfo / content shape.
 */

import { useWorkspaceStore } from '../workspace';
import type { CrossEngineMigrateTabContent } from '../workspace/core/types/workspaceTypes';

/** Connection metadata shown on the tab + reused as the migration source. */
type MigrateConnectionInfo = {
  connectionId: string;
  connectionName: string;
  serverHost?: string;
  database: string;
  engine: string;
};

/**
 * Open a "Migrate all tables to connection…" tab. `scope: 'database'` with
 * `subset: null` means "every table" until the user narrows it in the config
 * phase.
 */
export function openMigrateDatabaseTab(info: MigrateConnectionInfo): string {
  const content: CrossEngineMigrateTabContent = {
    scope: 'database',
    source: {
      connectionId: info.connectionId,
      database: info.database,
      engine: info.engine,
    },
    subset: null,
  };
  return useWorkspaceStore.getState().addTab({
    type: 'cross-engine-migrate',
    title: `Migrate ${info.database}`,
    connectionInfo: {
      connectionId: info.connectionId,
      connectionName: info.connectionName,
      serverHost: info.serverHost,
      database: info.database,
    },
    content,
  });
}

/**
 * Open a "Migrate Table to Connection…" tab seeded with a single table.
 * `scope: 'table'` carries `schema`/`table` on the source; `subset` is left
 * `undefined` (it only applies to database-scope migrations).
 */
export function openMigrateTableTab(
  info: MigrateConnectionInfo & { schema: string; table: string },
): string {
  const content: CrossEngineMigrateTabContent = {
    scope: 'table',
    source: {
      connectionId: info.connectionId,
      database: info.database,
      engine: info.engine,
      schema: info.schema,
      table: info.table,
    },
    subset: undefined,
  };
  return useWorkspaceStore.getState().addTab({
    type: 'cross-engine-migrate',
    title: `Migrate ${info.table}`,
    connectionInfo: {
      connectionId: info.connectionId,
      connectionName: info.connectionName,
      serverHost: info.serverHost,
      database: info.database,
    },
    content,
  });
}
